import React, { useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    DragOverEvent,
    useDroppable,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import UiContext from "../../../../contexts/UiContext";

import DashboardWidgetTypeEnum from "../../../../models/DashboardWidgetTypeEnum";
import { IMobileDashboardItem, ISupplierMobileDashboardSettings } from "../../../../models/IMobileDashboardConfig";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IUpdateSupplierSettingsDTO from "../../../../models/DTO/IUpdateSupplierSettingsDTO";
import IOnUpdateSupplierSettingsDTO from "../../../../models/DTO/IOnUpdateSupplierSettingsDTO";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import IBreadcrumbs from "../../../../models/IBreadcrumbs";
import ISupplierSettings from "../../../../models/ISupplierSettings";

import Breadcrumbs from "../../../shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwToggle from "../../../shared/OwToggle/OwToggle";

import "./AppDashboardConfig.scss";

// Widget metadata for display in palette
interface IWidgetMeta {
    type: DashboardWidgetTypeEnum;
    titleKey: string;
    iconClass: string;
}

// Hidden dashboard widgets for now.
const WIDGET_METADATA: IWidgetMeta[] = [
    // { type: DashboardWidgetTypeEnum.MONTHLY_ORDERS, titleKey: "monthly_orders", iconClass: "fad fa-shopping-cart" },
    // { type: DashboardWidgetTypeEnum.MONTHLY_SHIPMENTS, titleKey: "monthly_shipments", iconClass: "fad fa-truck" },
    { type: DashboardWidgetTypeEnum.DAILY_SALES, titleKey: "daily_sales", iconClass: "fad fa-dollar-sign" },
    { type: DashboardWidgetTypeEnum.WEEKLY_SALES, titleKey: "weekly_sales", iconClass: "fad fa-chart-line" },
    { type: DashboardWidgetTypeEnum.MONTHLY_SALES, titleKey: "monthly_sales", iconClass: "fad fa-chart-bar" },
    { type: DashboardWidgetTypeEnum.YEARLY_SALES, titleKey: "yearly_sales", iconClass: "fad fa-chart-pie" },
    // { type: DashboardWidgetTypeEnum.RECENT_ORDERS, titleKey: "recent_orders", iconClass: "fad fa-clipboard-list" },
    // { type: DashboardWidgetTypeEnum.ORDERS_AT_STEP, titleKey: "orders_at_step", iconClass: "fad fa-tasks" },
    // { type: DashboardWidgetTypeEnum.LAST_FIVE_INVENTORY, titleKey: "last_five_inventory", iconClass: "fad fa-boxes" },
    // { type: DashboardWidgetTypeEnum.DUE_DATE_CALENDAR, titleKey: "due_date_calendar", iconClass: "fad fa-calendar-alt" },
    // { type: DashboardWidgetTypeEnum.SYSTEM_MESSAGES, titleKey: "system_messages", iconClass: "fad fa-bell" },
    // { type: DashboardWidgetTypeEnum.FREIGHT_POLICY, titleKey: "freight_policy", iconClass: "fad fa-shipping-fast" },
    { type: DashboardWidgetTypeEnum.LAST_YEAR_VOLUME, titleKey: "last_year_volume", iconClass: "fad fa-chart-area" },
    // { type: DashboardWidgetTypeEnum.COOP_TOTAL, titleKey: "coop_total", iconClass: "fad fa-handshake" },
    // { type: DashboardWidgetTypeEnum.REBATE_TOTAL, titleKey: "rebate_total", iconClass: "fad fa-percent" },
];

// Dummy data for widget previews
const getDummyData = (widgetType: DashboardWidgetTypeEnum): any => {
    switch (widgetType) {
        case DashboardWidgetTypeEnum.MONTHLY_ORDERS:
            return { count: 1000 };
        case DashboardWidgetTypeEnum.MONTHLY_SHIPMENTS:
            return { count: 850 };
        case DashboardWidgetTypeEnum.DAILY_SALES:
            return { amount: 1250.0 };
        case DashboardWidgetTypeEnum.WEEKLY_SALES:
            return { amount: 8750.0 };
        case DashboardWidgetTypeEnum.MONTHLY_SALES:
            return { amount: 35000.0 };
        case DashboardWidgetTypeEnum.YEARLY_SALES:
            return { amount: 420000.0 };
        case DashboardWidgetTypeEnum.RECENT_ORDERS:
            return {
                orders: [
                    { name: "ORD-10001", status: "Processing" },
                    { name: "ORD-10002", status: "Shipped" },
                ],
            };
        case DashboardWidgetTypeEnum.ORDERS_AT_STEP:
            return { step: "Pending Review", count: 12 };
        case DashboardWidgetTypeEnum.LAST_FIVE_INVENTORY:
            return { items: ["Widget A", "Widget B", "Widget C"] };
        case DashboardWidgetTypeEnum.DUE_DATE_CALENDAR:
            return { upcomingDates: 5 };
        case DashboardWidgetTypeEnum.SYSTEM_MESSAGES:
            return { unread: 3 };
        case DashboardWidgetTypeEnum.FREIGHT_POLICY:
            return { threshold: 500 };
        case DashboardWidgetTypeEnum.LAST_YEAR_VOLUME:
            return { volume: 125000 };
        case DashboardWidgetTypeEnum.COOP_TOTAL:
            return { amount: 2500.0 };
        case DashboardWidgetTypeEnum.REBATE_TOTAL:
            return { amount: 1800.0 };
        default:
            return {};
    }
};

// Widget Palette Item (draggable source)
const WidgetPaletteItem: React.FC<{ widget: IWidgetMeta; isInPreview: boolean }> = ({ widget, isInPreview }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
        id: `palette-${widget.type}`,
        data: { type: "palette", widgetType: widget.type },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        opacity: isDragging ? 0.5 : isInPreview ? 0.6 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`widget-palette-item ${isInPreview ? "in-preview" : ""}`}>
            <i className={`${widget.iconClass} me-2`} />
            <OwLabel title={widget.titleKey} noBottomMargin={true} />
            {isInPreview && <i className="fas fa-check ms-auto text-success" />}
        </div>
    );
};

// Sortable Widget Preview Item (in mobile preview)
const SortableWidgetPreview: React.FC<{
    widget: IMobileDashboardItem;
    onRemove: () => void;
    onToggleVisibility: () => void;
}> = ({ widget, onRemove, onToggleVisibility }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: widget.uuid,
        data: { type: "preview", widget },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : widget.isVisible ? 1 : 0.5,
    };

    const meta = WIDGET_METADATA.find((m) => m.type === widget.widgetType);
    const dummyData = getDummyData(widget.widgetType);

    const renderDummyContent = () => {
        switch (widget.widgetType) {
            case DashboardWidgetTypeEnum.MONTHLY_ORDERS:
            case DashboardWidgetTypeEnum.MONTHLY_SHIPMENTS:
                return <div className="widget-value">{dummyData.count?.toLocaleString()}</div>;
            case DashboardWidgetTypeEnum.DAILY_SALES:
            case DashboardWidgetTypeEnum.WEEKLY_SALES:
            case DashboardWidgetTypeEnum.MONTHLY_SALES:
            case DashboardWidgetTypeEnum.YEARLY_SALES:
            case DashboardWidgetTypeEnum.COOP_TOTAL:
            case DashboardWidgetTypeEnum.REBATE_TOTAL:
                return <div className="widget-value">${dummyData.amount?.toLocaleString()}</div>;
            case DashboardWidgetTypeEnum.ORDERS_AT_STEP:
                return (
                    <div className="widget-value">
                        {dummyData.count} orders at {dummyData.step}
                    </div>
                );
            case DashboardWidgetTypeEnum.SYSTEM_MESSAGES:
                return <div className="widget-value">{dummyData.unread} unread</div>;
            case DashboardWidgetTypeEnum.DUE_DATE_CALENDAR:
                return <div className="widget-value">{dummyData.upcomingDates} upcoming</div>;
            default:
                return <div className="widget-value"></div>;
        }
    };

    return (
        <div ref={setNodeRef} style={style} className={`mobile-widget-preview ${!widget.isVisible ? "hidden-widget" : ""}`}>
            <div className="widget-drag-handle" {...attributes} {...listeners}>
                <i className="fas fa-grip-vertical" />
            </div>
            <div className="widget-content">
                <div className="widget-header">
                    <i className={`${meta?.iconClass || "fad fa-cube"} me-2`} />
                    <OwLabel title={meta?.titleKey || widget.widgetType} noBottomMargin={true} />
                </div>
                {renderDummyContent()}
            </div>
            <div className="widget-actions">
                <button
                    className={`btn btn-sm ${widget.isVisible ? "btn-outline-secondary" : "btn-outline-warning"}`}
                    onClick={onToggleVisibility}
                    title={widget.isVisible ? "Hide widget" : "Show widget"}>
                    <i className={widget.isVisible ? "fas fa-eye" : "fas fa-eye-slash"} />
                </button>
                <button className="btn btn-sm btn-outline-danger" onClick={onRemove} title="Remove widget">
                    <i className="fas fa-times" />
                </button>
            </div>
        </div>
    );
};

// Mobile Preview Drop Zone
const MobilePreviewDropZone: React.FC<{
    widgets: IMobileDashboardItem[];
    onRemove: (uuid: string) => void;
    onToggleVisibility: (uuid: string) => void;
}> = ({ widgets, onRemove, onToggleVisibility }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: "mobile-preview-zone",
    });

    return (
        <div ref={setNodeRef} className={`mobile-preview-zone ${isOver ? "drag-over" : ""}`}>
            <div className="phone-frame">
                <div className="phone-notch" />
                <div className="phone-screen">
                    {widgets.length === 0 ? (
                        <div className="empty-state">
                            <i className="fad fa-hand-pointer fa-2x mb-2" />
                            <OwLabel title="drag_widgets_here" noBottomMargin={true} />
                        </div>
                    ) : (
                        <SortableContext items={widgets.map((w) => w.uuid)} strategy={verticalListSortingStrategy}>
                            {widgets.map((widget) => (
                                <SortableWidgetPreview
                                    key={widget.uuid}
                                    widget={widget}
                                    onRemove={() => onRemove(widget.uuid)}
                                    onToggleVisibility={() => onToggleVisibility(widget.uuid)}
                                />
                            ))}
                        </SortableContext>
                    )}
                </div>
                <div className="phone-home-indicator" />
            </div>
        </div>
    );
};

// Main Component
export const AppDashboardConfig = () => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    // State
    const [widgets, setWidgets] = useState<IMobileDashboardItem[]>([]);
    const [availableWidgetTypes, setAvailableWidgetTypes] = useState<DashboardWidgetTypeEnum[]>(WIDGET_METADATA.map((w) => w.type));
    const [allowedCustomizableWidgets, setAllowedCustomizableWidgets] = useState<DashboardWidgetTypeEnum[]>([]);
    const [allowWidgetReordering, setAllowWidgetReordering] = useState(true);
    const [allowWidgetVisibilityToggle, setAllowWidgetVisibilityToggle] = useState(true);
    const [pendingChanges, setPendingChanges] = useState(false);
    const [saveActive, setSaveActive] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);

    const crumbs: IBreadcrumbs[] = [
        { title: "business_settings", url: "/business/settings" },
        { title: "app_dashboard_config", url: "/business/settings/app-dashboard-config" },
    ];

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Load initial config from supplier settings
    useEffect(() => {
        if (supplierModel?.settings) {
            const settings = typeof supplierModel.settings === "string" ? JSON.parse(supplierModel.settings) : supplierModel.settings;

            if (settings?.mobileDashboard) {
                const mobileSettings = settings.mobileDashboard as ISupplierMobileDashboardSettings;
                setWidgets(mobileSettings.baselineConfig?.widgets || []);
                setAvailableWidgetTypes(mobileSettings.availableWidgetTypes || WIDGET_METADATA.map((w) => w.type));
                setAllowedCustomizableWidgets(mobileSettings.allowedCustomizableWidgets || []);
                setAllowWidgetReordering(mobileSettings.allowWidgetReordering ?? true);
                setAllowWidgetVisibilityToggle(mobileSettings.allowWidgetVisibilityToggle ?? true);
            }
        }
    }, [supplierModel]);

    // Socket listener for save response
    useEffect(() => {
        const onUpdateSettings = (_data: IOnUpdateSupplierSettingsDTO) => {
            setSaveActive(false);
            setPendingChanges(false);
            uiContext.notifyFn(null, {
                message: "app_dashboard_config_saved",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
        };

        socket.on(EndpointsEnum.SupplierOnUpdateSupplierSettings, onUpdateSettings);

        return () => {
            socket.removeListener(EndpointsEnum.SupplierOnUpdateSupplierSettings, onUpdateSettings);
        };
    }, [socket, uiContext]);

    // Drag handlers
    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragOver = (_event: DragOverEvent) => {
        // Handle drag over for visual feedback if needed
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        const activeData = active.data.current;
        const overId = over.id;

        // Dropping from palette to preview zone
        if (activeData?.type === "palette" && (overId === "mobile-preview-zone" || widgets.some((w) => w.uuid === overId))) {
            const widgetType = activeData.widgetType as DashboardWidgetTypeEnum;

            // Check if widget already exists (for non-multiple widgets)
            const existingWidget = widgets.find((w) => w.widgetType === widgetType);
            if (existingWidget) {
                // Widget already in preview - don't add duplicate
                return;
            }

            const newWidget: IMobileDashboardItem = {
                uuid: uuidv4(),
                widgetType,
                layout: { x: 0, y: widgets.length, w: 1, h: 1 },
                isVisible: true,
            };

            // Find insertion index if dropping over an existing widget
            let insertIndex = widgets.length;
            if (overId !== "mobile-preview-zone") {
                const overIndex = widgets.findIndex((w) => w.uuid === overId);
                if (overIndex !== -1) {
                    insertIndex = overIndex;
                }
            }

            const newWidgets = [...widgets];
            newWidgets.splice(insertIndex, 0, newWidget);
            setWidgets(newWidgets);
            setPendingChanges(true);
            return;
        }

        // Reordering within preview
        if (activeData?.type === "preview" && active.id !== over.id) {
            const oldIndex = widgets.findIndex((w) => w.uuid === active.id);
            const newIndex = widgets.findIndex((w) => w.uuid === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                setWidgets(arrayMove(widgets, oldIndex, newIndex));
                setPendingChanges(true);
            }
        }
    };

    // Widget actions
    const removeWidget = (uuid: string) => {
        setWidgets(widgets.filter((w) => w.uuid !== uuid));
        setPendingChanges(true);
    };

    const toggleWidgetVisibility = (uuid: string) => {
        setWidgets(widgets.map((w) => (w.uuid === uuid ? { ...w, isVisible: !w.isVisible } : w)));
        setPendingChanges(true);
    };

    // Permission toggles
    const handleToggleReordering = () => {
        setAllowWidgetReordering(!allowWidgetReordering);
        setPendingChanges(true);
    };

    const handleToggleVisibility = () => {
        setAllowWidgetVisibilityToggle(!allowWidgetVisibilityToggle);
        setPendingChanges(true);
    };

    // Save configuration
    const saveConfig = () => {
        const mobileDashboardSettings: ISupplierMobileDashboardSettings = {
            baselineConfig: { widgets },
            availableWidgetTypes,
            allowedCustomizableWidgets,
            allowWidgetReordering,
            allowWidgetVisibilityToggle,
        };

        const currentSettings =
            typeof supplierModel.settings === "string" ? JSON.parse(supplierModel.settings) : supplierModel.settings || {};

        const payload: IUpdateSupplierSettingsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            settings: {
                ...currentSettings,
                mobileDashboard: mobileDashboardSettings,
            } as ISupplierSettings,
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, payload);
        setSaveActive(true);
    };

    // Reset changes
    const resetChanges = () => {
        if (supplierModel?.settings) {
            const settings = typeof supplierModel.settings === "string" ? JSON.parse(supplierModel.settings) : supplierModel.settings;

            if (settings?.mobileDashboard) {
                const mobileSettings = settings.mobileDashboard as ISupplierMobileDashboardSettings;
                setWidgets(mobileSettings.baselineConfig?.widgets || []);
                setAllowWidgetReordering(mobileSettings.allowWidgetReordering ?? true);
                setAllowWidgetVisibilityToggle(mobileSettings.allowWidgetVisibilityToggle ?? true);
            } else {
                setWidgets([]);
            }
        }
        setPendingChanges(false);
    };

    // Get active drag overlay content
    const getActiveWidget = () => {
        if (!activeId) return null;

        if (activeId.startsWith("palette-")) {
            const widgetType = activeId.replace("palette-", "") as DashboardWidgetTypeEnum;
            const meta = WIDGET_METADATA.find((w) => w.type === widgetType);
            if (meta) {
                return (
                    <div className="widget-palette-item dragging">
                        <i className={`${meta.iconClass} me-2`} />
                        <OwLabel title={meta.titleKey} noBottomMargin={true} />
                    </div>
                );
            }
        } else {
            const widget = widgets.find((w) => w.uuid === activeId);
            if (widget) {
                const meta = WIDGET_METADATA.find((m) => m.type === widget.widgetType);
                return (
                    <div className="mobile-widget-preview dragging">
                        <div className="widget-drag-handle">
                            <i className="fas fa-grip-vertical" />
                        </div>
                        <div className="widget-content">
                            <div className="widget-header">
                                <i className={`${meta?.iconClass || "fad fa-cube"} me-2`} />
                                <OwLabel title={meta?.titleKey || widget.widgetType} noBottomMargin={true} />
                            </div>
                        </div>
                    </div>
                );
            }
        }
        return null;
    };

    // Check which widgets are already in preview
    const widgetsInPreview = new Set(widgets.map((w) => w.widgetType));

    return (
        <div className="AppDashboardConfig">
            <Breadcrumbs crumbs={crumbs} />

            <div className="d-flex align-items-center justify-content-between mb-3">
                <OwTitle title="app_dashboard_config" leftIconName="fad fa-mobile-alt" />
                <div className="d-flex align-items-center">
                    {pendingChanges && (
                        <>
                            <OwLabel
                                leftIconClass="fad fa-exclamation-triangle"
                                title="pending_changes"
                                noBottomMargin={true}
                                className="text-danger me-2"
                            />
                            <OwButton leftIconClass="fas fa-undo" title="reset" fn={resetChanges} className="me-2 btn-warning" />
                        </>
                    )}
                    <OwButton
                        id="save-config"
                        title="save"
                        leftIconClass="fad fa-save"
                        fn={saveConfig}
                        active={saveActive}
                        disabled={!pendingChanges}
                    />
                </div>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}>
                <div className="config-layout">
                    <div className="palette-section">
                        <OwLabel title="available_widgets" isBold={true} isUnderline={true} />
                        <p className="text-muted small mb-3">
                            <OwLabel title="drag_widgets_to_preview" noBottomMargin={true} />
                        </p>

                        <SortableContext items={WIDGET_METADATA.map((w) => `palette-${w.type}`)} strategy={verticalListSortingStrategy}>
                            <div className="widget-palette">
                                {WIDGET_METADATA.map((widget) => (
                                    <WidgetPaletteItem key={widget.type} widget={widget} isInPreview={widgetsInPreview.has(widget.type)} />
                                ))}
                            </div>
                        </SortableContext>

                        {/* TODO: V2 Add permissions section back in */}
                        {/* <div className="permissions-section mt-4">
                            <OwLabel title="user_permissions" isBold={true} isUnderline={true} className="mb-2" />
                            <OwToggle
                                name="allowWidgetReordering"
                                label="allow_widget_reordering"
                                fn={handleToggleReordering}
                                active={allowWidgetReordering}
                            />
                            <OwToggle
                                name="allowWidgetVisibilityToggle"
                                label="allow_widget_visibility_toggle"
                                fn={handleToggleVisibility}
                                active={allowWidgetVisibilityToggle}
                            />
                        </div> */}
                    </div>

                    <div className="preview-section">
                        <OwLabel title="mobile_preview" isBold={true} isUnderline={true} className="mb-2" />
                        <MobilePreviewDropZone widgets={widgets} onRemove={removeWidget} onToggleVisibility={toggleWidgetVisibility} />
                    </div>
                </div>

                <DragOverlay>{getActiveWidget()}</DragOverlay>
            </DndContext>
        </div>
    );
};
